import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, TrendingUp, ArrowUpRight, Scale, CreditCard, Landmark } from 'lucide-react';
import { BusinessReports } from '../components/BusinessReports';
import { ProfitSweep } from '../components/ProfitSweep';
import { PlatformInvoices } from '../components/PlatformInvoices';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminPlatformFinancials() {
  const [data, setData] = useState(null);
  const [books, setBooks] = useState(null);
  const [booksDays, setBooksDays] = useState(30);
  const [booksLoading, setBooksLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/platform-financials`, auth())
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load platform financials'));
  }, []);

  useEffect(() => {
    setBooksLoading(true);
    axios.get(`${API}/admin/platform-financials/books`, { ...auth(), params: { days: booksDays } })
      .then(r => setBooks(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load Stripe activity'))
      .finally(() => setBooksLoading(false));
  }, [booksDays]);

  if (!data) return <div className="min-h-screen bg-background pt-28 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const { revenue, monthly, top_events, money_out, liabilities, stripe_balance, treasury } = data;
  const out30 = money_out.last_30d; const outAll = money_out.all_time;
  const outRows = [
    ['Organizer bank withdrawals', 'organizer_withdrawals'],
    ['Staff / vendor / venue payments', 'staff_vendor_payments'],
    ['Wallet credits sent', 'wallet_credits_sent'],
    ['User bank withdrawals', 'user_bank_withdrawals'],
  ];
  const totalOutAll = outRows.reduce((s, [, k]) => s + (outAll[k]?.cents || 0), 0);
  const totalOut30 = outRows.reduce((s, [, k]) => s + (out30[k]?.cents || 0), 0);

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 space-y-6" data-testid="platform-financials-page">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><TrendingUp className="h-6 w-6 text-emerald-400" /> Eventism Platform Financials</h1>
          <p className="text-sm text-muted-foreground">What the platform earns, pays out, and owes — across every event and partner.</p>
        </div>

        <div>
          <p className="text-sm font-semibold mb-2">Platform revenue (service fees)</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[['All time', revenue.all_time], ['Last 30 days', revenue.last_30d], ['Last 7 days', revenue.last_7d]].map(([label, r]) => (
              <Card key={label} className="border-emerald-500/30"><CardContent className="py-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
                <p className="text-2xl font-extrabold text-emerald-400" data-testid={`rev-${label.toLowerCase().replace(/ /g, '-')}`}>{usd(r.fees_cents)}</p>
                <p className="text-[11px] text-muted-foreground">{r.orders.toLocaleString()} orders · {usd(r.gross_cents)} gross sales</p>
              </CardContent></Card>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-semibold mb-2">Monthly fees (12 months)</p>
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="monthly-table">
                <thead><tr className="bg-muted/40"><th className="text-left px-3 py-2">Month</th><th className="text-right px-3 py-2">Fees earned</th><th className="text-right px-3 py-2">Gross</th><th className="text-right px-3 py-2">Orders</th></tr></thead>
                <tbody>
                  {monthly.length === 0 ? <tr><td colSpan={4} className="px-3 py-5 text-center text-muted-foreground text-xs">No data yet.</td></tr>
                    : [...monthly].reverse().map(m => (
                      <tr key={m.month} className="border-t border-border/60">
                        <td className="px-3 py-1.5">{m.month}</td>
                        <td className="px-3 py-1.5 text-right font-semibold text-emerald-400">{usd(m.fees)}</td>
                        <td className="px-3 py-1.5 text-right">{usd(m.gross)}</td>
                        <td className="px-3 py-1.5 text-right">{m.orders}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Top events by fees earned</p>
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="top-events-table">
                <thead><tr className="bg-muted/40"><th className="text-left px-3 py-2">Event</th><th className="text-right px-3 py-2">Fees</th><th className="text-right px-3 py-2">Gross</th><th className="text-right px-3 py-2">Orders</th></tr></thead>
                <tbody>
                  {top_events.map(t => (
                    <tr key={t.event_id || t.event_name} className="border-t border-border/60">
                      <td className="px-3 py-1.5 text-xs">{t.event_name}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-emerald-400">{usd(t.fees)}</td>
                      <td className="px-3 py-1.5 text-right">{usd(t.gross)}</td>
                      <td className="px-3 py-1.5 text-right">{t.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><ArrowUpRight className="h-4 w-4 text-rose-400" /> Money out</p>
          <div className="rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="money-out-table">
              <thead><tr className="bg-muted/40"><th className="text-left px-3 py-2">Type</th><th className="text-right px-3 py-2">Last 30 days</th><th className="text-right px-3 py-2">All time</th></tr></thead>
              <tbody>
                {outRows.map(([label, k]) => (
                  <tr key={k} className="border-t border-border/60">
                    <td className="px-3 py-1.5">{label}</td>
                    <td className="px-3 py-1.5 text-right">{usd(out30[k]?.cents)} <span className="text-[10px] text-muted-foreground">({out30[k]?.count || 0})</span></td>
                    <td className="px-3 py-1.5 text-right font-semibold text-rose-400">{usd(outAll[k]?.cents)} <span className="text-[10px] text-muted-foreground">({outAll[k]?.count || 0})</span></td>
                  </tr>
                ))}
                <tr className="border-t border-border bg-muted/20 font-bold">
                  <td className="px-3 py-1.5">Total out</td>
                  <td className="px-3 py-1.5 text-right">{usd(totalOut30)}</td>
                  <td className="px-3 py-1.5 text-right text-rose-400">{usd(totalOutAll)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <Card className="border-amber-500/30"><CardContent className="py-4 space-y-1">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Scale className="h-4 w-4 text-amber-400" /> Wallet liabilities (you owe)</p>
            <p className="text-2xl font-extrabold text-amber-400" data-testid="liabilities-total">{usd(liabilities.total_cents)}</p>
            <p className="text-[11px] text-muted-foreground">Organizer wallets: {usd(liabilities.org_wallets_cents)} ({liabilities.org_wallets_count}) · User wallets: {usd(liabilities.user_wallets_cents)} ({liabilities.user_wallets_count})</p>
          </CardContent></Card>
          <Card className="border-indigo-500/30"><CardContent className="py-4 space-y-1">
            <p className="text-sm font-semibold flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-indigo-400" /> Eventism Treasury</p>
            {stripe_balance ? (
              <>
                <p className="text-2xl font-extrabold" data-testid="stripe-available">{usd(stripe_balance.available_cents)} <span className="text-xs font-normal text-muted-foreground">available</span></p>
                <p className="text-[11px] text-muted-foreground">Pending from recent sales: {usd(stripe_balance.pending_cents)}</p>
              </>
            ) : <p className="text-xs text-muted-foreground">Stripe balance unavailable.</p>}
          </CardContent></Card>
          <Card className="border-emerald-500/40"><CardContent className="py-4 space-y-1">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Landmark className="h-4 w-4 text-emerald-400" /> Platform Profits</p>
            {treasury ? (
              <>
                <p className={`text-2xl font-extrabold ${treasury.cents >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="treasury-total">{usd(treasury.cents)}</p>
                <p className="text-[11px] text-muted-foreground">Yours free &amp; clear — Treasury funds {usd(treasury.held_cents)} minus {usd(treasury.liabilities_cents)} owed to wallets</p>
              </>
            ) : <p className="text-xs text-muted-foreground">Needs a live Stripe balance to compute.</p>}
          </CardContent></Card>
        </div>

        <ProfitSweep />

        <PlatformInvoices />

        <BusinessReports />

        {/* Balance the Books — real Stripe account activity */}
        <div data-testid="balance-books-section">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Scale className="h-4 w-4 text-fuchsia-400" /> Balance the Books — Stripe account activity</p>
            <select value={booksDays} onChange={e => setBooksDays(parseInt(e.target.value))}
              className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="books-days-select">
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
          </div>
          {booksLoading || !books ? (
            <Card className="border-border"><CardContent className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></CardContent></Card>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                {[
                  ['Money in', books.summary.money_in_cents, 'text-emerald-400', `${books.summary.money_in_count} charges`],
                  ['Refunds', books.summary.refunds_cents, 'text-rose-400', `${books.summary.refunds_count}`],
                  ['Stripe fees', books.summary.stripe_fees_cents, 'text-rose-400', 'processing'],
                  ['Partner transfers', books.summary.transfers_cents, 'text-rose-400', `${books.summary.transfers_count} payouts`],
                  ['Owner draws', books.summary.owner_draws_cents, 'text-amber-400', `${books.summary.owner_draws_count} bank payouts`],
                  ['Card spending', books.summary.card_spend_cents, 'text-amber-400', 'business cards'],
                  ['Net change', books.summary.net_cents, books.summary.net_cents >= 0 ? 'text-emerald-400' : 'text-rose-400', `${books.transactions_scanned} transactions`],
                ].map(([label, cents, color, sub]) => (
                  <Card key={label} className="border-border"><CardContent className="py-2.5 px-3">
                    <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
                    <p className={`text-sm font-extrabold ${color}`} data-testid={`books-${label.toLowerCase().replace(/ /g, '-')}`}>{usd(cents)}</p>
                    <p className="text-[10px] text-muted-foreground">{sub}</p>
                  </CardContent></Card>
                ))}
              </div>

              {!books.issuing_enabled ? (
                <p className="text-[11px] text-muted-foreground">Business card detail unavailable — Stripe Issuing isn't enabled on this account (card spend still appears in the category totals if present).</p>
              ) : books.cards.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">No business card transactions in this period.</p>
              ) : (
                <Card className="border-border"><CardContent className="py-3">
                  <p className="text-xs font-semibold flex items-center gap-1.5 mb-2"><CreditCard className="h-3.5 w-3.5 text-indigo-400" /> Business card spending (owner & event purchases)</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {books.cards.map(c => (
                      <span key={c.card} className="text-[11px] px-2.5 py-1 rounded-full border border-border" data-testid={`card-chip-${c.card.replace(/\W/g, '')}`}>
                        {c.card}{c.holder ? ` · ${c.holder}` : ''} — <strong>{usd(Math.abs(c.cents))}</strong> ({c.count})
                      </span>
                    ))}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="card-transactions-table">
                      <thead><tr className="text-left text-muted-foreground border-b border-border">
                        <th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Card</th><th className="px-2 py-1.5">Merchant</th><th className="px-2 py-1.5">Category</th><th className="px-2 py-1.5 text-right">Amount</th>
                      </tr></thead>
                      <tbody>
                        {books.card_transactions.map((t, i) => (
                          <tr key={i} className="border-b border-border/50">
                            <td className="px-2 py-1.5 whitespace-nowrap">{t.date}</td>
                            <td className="px-2 py-1.5 whitespace-nowrap">{t.card}</td>
                            <td className="px-2 py-1.5">{t.merchant}</td>
                            <td className="px-2 py-1.5 capitalize text-muted-foreground">{t.category}</td>
                            <td className={`px-2 py-1.5 text-right font-semibold ${t.amount_cents < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{usd(t.amount_cents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent></Card>
              )}

              <Card className="border-border"><CardContent className="py-3">
                <p className="text-xs font-semibold mb-2">Every Stripe category this period</p>
                <div className="flex flex-wrap gap-2">
                  {books.categories.map(c => (
                    <span key={c.category} className="text-[11px] px-2.5 py-1 rounded-full border border-border capitalize">
                      {c.category}: <strong className={c.cents < 0 ? 'text-rose-400' : 'text-emerald-400'}>{usd(c.cents)}</strong> ({c.count})
                    </span>
                  ))}
                </div>
              </CardContent></Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
